import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { CTABand } from '../components/CTABand';
export function Programs() {
  useEffect(() => {
    document.title = 'Martial Arts Programs | Storm Taekwondo';
  }, []);
  const programs = [{
    title: 'Mini Troopers',
    age: 'Ages 3-5',
    description: 'Perfect for active preschoolers who need a positive outlet for their energy. Our Mini Troopers program focuses on basic listening skills, coordination, sharing on the mat, and following directions in a fun, structured environment.',
    benefits: ['Listening Skills', 'Coordination', 'Focus', 'Respect'],
    image: 'https://drwzpk38qkpfb.cloudfront.net/www.stormtaekwondo.com/uploaded/images/Redesigne%202024/Mini-Troopers-Storm-Taekwondo.jpg',
    href: '/mini-troopers'
  }, {
    title: 'Kids Taekwondo',
    age: 'Ages 6+',
    description: 'Structured classes that teach respect, goal setting, and self-defense fundamentals. We help kids build unshakeable confidence and discipline that carries over into their schoolwork and home life.',
    benefits: ['Confidence Building', 'Discipline', 'Self-Defense', 'Goal Setting'],
    image: 'https://drwzpk38qkpfb.cloudfront.net/www.stormtaekwondo.com/uploaded/images/Redesigne%202024/Discover%20the%20Perfect%20Program%20for%20Your%20Mini%20Trooper.png',
    href: '/kids-taekwondo-classes'
  }, {
    title: 'Adult Taekwondo',
    age: 'Adults',
    description: 'Beginner-friendly classes focused on fitness, confidence, and practical martial arts skills. Whether you want to get in shape, learn self-defense, or find a new hobby, our adult classes provide a supportive environment.',
    benefits: ['Physical Fitness', 'Stress Relief', 'Self-Defense', 'Flexibility'],
    image: 'https://cdn.magicpatterns.com/uploads/t7AgirMmJivJBXyaULHJUS/Adult.png',
    href: '/adult-taekwondo-classes'
  }, {
    title: 'Family Classes',
    age: 'All Ages',
    description: 'Families looking for a healthy, active hobby they can do on the same mat. Train together, grow together, and build confidence, fitness, and discipline as a family at Storm Taekwondo.',
    benefits: ['Family Bonding', 'Shared Goals', 'Fitness Together', 'Convenience'],
    image: 'https://cdn.magicpatterns.com/uploads/jpxHBCPtqv9Pv8vdpeiuPD/Family.png',
    href: '/family-martial-arts-classes'
  }, {
    title: 'Self-Defense',
    age: 'Teens & Adults',
    description: 'Build confidence, awareness, fitness, and practical martial arts fundamentals in a structured Taekwondo environment. Learn how to protect yourself while getting a great workout.',
    benefits: ['Situational Awareness', 'Practical Techniques', 'Confidence', 'Fitness'],
    image: 'https://cdn.magicpatterns.com/uploads/hA5TYvHR3WY9BEhHeDQTE4/SelfDefense.png',
    href: '/self-defense-classes'
  }, {
    title: 'Competition Team',
    age: 'Invite Only / Tryouts',
    description: 'For students ready to compete at a higher level. Led by experienced coaches including a 2x USA National Sparring Champion, this program prepares athletes for local, state, and national Taekwondo tournaments.',
    benefits: ['Elite Coaching', 'Tournament Prep', 'Advanced Sparring', 'Mental Toughness'],
    image: 'https://cdn.magicpatterns.com/uploads/36vtLMum6Tc21F2FXt8jPS/Comp.png',
    href: '/competition-team'
  }];
  return <div className="flex flex-col bg-off-white" data-id="element-651">
      {/* Hero */}
      <section className="bg-ink py-20 text-center px-4" data-id="element-652">
        <h1 className="text-5xl md:text-6xl text-white mb-6" data-id="element-653">
          Programs Built for Every Storm Student
        </h1>
        <p className="text-xl text-gray-warm max-w-2xl mx-auto" data-id="element-654">
          From energetic 3-year-olds to adults looking for a new challenge, we
          have a structured, supportive class for you.
        </p>
      </section>

      {/* Programs List */}
      <section className="py-12" data-id="element-655">
        {programs.map((program, idx) => <div key={idx} className={`py-16 ${idx % 2 === 0 ? 'bg-off-white' : 'bg-paper border-y border-subtle-gray'}`} data-id="element-656">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-657">
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`} data-id="element-658">
                <div className={idx % 2 === 1 ? 'lg:order-2' : ''} data-id="element-659">
                  <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-660">
                    <img src={program.image} alt={program.title} className="w-full h-full object-cover" data-id="element-661" />
                    <div className="absolute top-4 left-4 bg-storm-red text-white px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide" data-id="element-662">
                      {program.age}
                    </div>
                  </div>
                </div>
                <div className={idx % 2 === 1 ? 'lg:order-1' : ''} data-id="element-663">
                  <h2 className="text-4xl text-ink mb-6" data-id="element-664">{program.title}</h2>
                  <p className="text-lg text-gray-warm mb-8 leading-relaxed" data-id="element-665">
                    {program.description}
                  </p>

                  <div className="mb-8" data-id="element-666">
                    <h4 className="font-bold text-ink mb-4 uppercase tracking-wide" data-id="element-667">
                      What Students Learn
                    </h4>
                    <ul className="grid grid-cols-2 gap-3" data-id="element-668">
                      {program.benefits.map((benefit, bIdx) => <li key={bIdx} className="flex items-center gap-2 text-gray-warm" data-id="element-669">
                          <CheckCircle2 className="w-5 h-5 text-storm-red shrink-0" data-id="element-670" />
                          <span data-id="element-671">{benefit}</span>
                        </li>)}
                    </ul>
                  </div>

                  <Link to={program.href} className="inline-flex items-center gap-2 btn-primary" data-id="element-672">
                    Learn More <ArrowRight className="w-5 h-5" data-id="element-673" />
                  </Link>
                </div>
              </div>
            </div>
          </div>)}
      </section>

      <CTABand headline="Not Sure Which Program is Right?" copy="Claim your free week and our coaches will help you find the perfect class based on your age, experience, and goals." data-id="element-674" />
    </div>;
}